import React, { useState } from 'react';
import { AddApiKeyForm } from './AddApiKeyForm';
import { ApiKeyList } from './ApiKeyList';
import { useApiKeys } from '../../hooks/useApiKeys';

/**
 * Main API Key management component
 * Orchestrates the add form and key list
 */
export const ApiKeyManager: React.FC = () => {
  const [showAddForm, setShowAddForm] = useState(false);
  const { keys, loading } = useApiKeys();

  const hasKeys = keys.length > 0;
  const hasDefaultClaude = keys.some(k => k.keyType === 'claude' && k.isDefault);
  const hasDefaultTavily = keys.some(k => k.keyType === 'tavily' && k.isDefault);

  return (
    <div className="space-y-6">
      {/* Warning Banners for Missing Required Keys */}
      {!hasDefaultClaude && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <h4 className="text-sm font-semibold text-red-900 mb-1">
                No Claude API key configured
              </h4>
              <p className="text-sm text-red-700">
                Add a Claude API key below to use AI features including research generation, persona creation, simulations, and evaluations.
              </p>
            </div>
          </div>
        </div>
      )}

      {!hasDefaultTavily && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <h4 className="text-sm font-semibold text-red-900 mb-1">
                No Tavily API key configured
              </h4>
              <p className="text-sm text-red-700">
                Add a Tavily API key below to enable web research for gathering market data, competitor analysis, and trend information.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h4 className="text-sm font-semibold text-blue-900 mb-1">
              About API Keys
            </h4>
            <div className="text-sm text-blue-700 space-y-1">
              <p>
                InsightStudio Labs requires API keys to access external services:
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li><strong>Claude (Required):</strong> Powers AI features like research and persona generation</li>
                <li><strong>Tavily (Required):</strong> Enables web search for gathering market insights</li>
                <li><strong>Gemini (Optional):</strong> Generates AI-powered persona avatar images</li>
              </ul>
              <p className="mt-2 text-xs">
                Your API keys are encrypted and stored securely in Firebase. We never see or store your plaintext keys.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Add New Key Button */}
      {!showAddForm && (
        <button
          onClick={() => setShowAddForm(true)}
          className="w-full sm:w-auto px-6 py-3 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
        >
          <div className="flex items-center justify-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add API Key
          </div>
        </button>
      )}

      {/* Add API Key Form */}
      {showAddForm && (
        <AddApiKeyForm
          onSuccess={() => setShowAddForm(false)}
          onCancel={() => setShowAddForm(false)}
        />
      )}

      {/* API Keys List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center space-x-3">
            <svg className="animate-spin h-6 w-6 text-primary-500" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-gray-600">Loading API keys...</span>
          </div>
        </div>
      ) : hasKeys ? (
        <ApiKeyList keys={keys} />
      ) : !showAddForm ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">No API keys yet</h3>
          <p className="mt-2 text-sm text-gray-500">
            Add your API keys above to start using InsightStudio Labs features
          </p>
        </div>
      ) : null}
    </div>
  );
};
